import { SxProps } from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField";
import { gql } from "generated-graphql";
import { MultipleFacilityPickerFragment } from "generated-graphql/graphql";
import React, { forwardRef, useCallback } from "react";
import { FieldError } from "react-hook-form";

import { useMultiPickerWithHydration, PickerInput } from "../hooks/useMultiPickerWithHydration";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import { ErrorDisplay } from "./Forms/ErrorDisplay";

gql(`
  fragment MultipleFacilityPicker on Facility {
    id
    name
  }
`);

const FACILITIES_QUERY = gql(`
  query PickerFacilitiesQuery($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...MultipleFacilityPicker
      }
      count
    }
  }
`);

const WASTE_ENABLED_FACILITIES_QUERY = gql(`
  query WasteEnabledMultipleFacilityPickerQuery($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities: wasteEnabledFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...MultipleFacilityPicker
      }
      count
    }
  }
`);

type FacilityQueryVariant = "all" | "waste";

interface MultipleFacilityPickerProps {
  onChange: (facilities: MultipleFacilityPickerFragment[] | null) => void;
  defaultSearchTerm?: string;
  disabledFacilitiesById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?:
    | (MultipleFacilityPickerFragment | PickerInput<MultipleFacilityPickerFragment>)[]
    | null
    | undefined;
  disabled?: boolean;
  showErrors?: boolean;
  error?: FieldError;
  sx?: SxProps;
  onOpen?: () => void;
  onClose?: () => void;
  facilityQueryVariant?: FacilityQueryVariant;
  hideErrors?: boolean;
  /** Forwarded to the underlying `AsyncAutocomplete` `TextField` (e.g. `size: "small"`). */
  textFieldProps?: TextFieldProps;
}

export const MultipleFacilityPicker: React.FC<MultipleFacilityPickerProps> = forwardRef<
  HTMLDivElement,
  MultipleFacilityPickerProps
>(function MultipleFacilityPicker(
  {
    onChange,
    defaultSearchTerm = "",
    disabledFacilitiesById = [],
    disabledOptionLabel = "",
    label = "Facility",
    value,
    error,
    disabled,
    showErrors = false,
    sx,
    onOpen,
    onClose,
    facilityQueryVariant = "all",
    hideErrors,
    textFieldProps,
  },
  ref
) {
  const getItems = useCallback((data: any) => data.facilities?.items ?? [], []);

  const { resolvedValue, isHydrating, enhancedOnChange, enhancedGetItems } =
    useMultiPickerWithHydration<MultipleFacilityPickerFragment, any>({
      value,
      query: facilityQueryVariant === "waste" ? WASTE_ENABLED_FACILITIES_QUERY : FACILITIES_QUERY,
      getItems,
      idsSearchPrefix: "ids",
      onChange,
    });

  return (
    <>
      <AsyncAutocomplete
        ref={ref}
        query={facilityQueryVariant === "waste" ? WASTE_ENABLED_FACILITIES_QUERY : FACILITIES_QUERY}
        initialPageSize={20}
        variables={{}}
        getItems={enhancedGetItems}
        getOptionLabel={(facility) => facility.name ?? ""}
        placeholderLabel={label}
        onChange={enhancedOnChange}
        value={resolvedValue}
        error={error}
        disabled={disabled || isHydrating}
        additionalSearchString={defaultSearchTerm}
        multiple={true}
        filterSelectedOptions
        getOptionDisabled={(option: MultipleFacilityPickerFragment) =>
          disabledFacilitiesById.includes(option?.id ?? "")
        }
        loading={isHydrating}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
            {disabledFacilitiesById.includes(option?.id ?? "") && (
              <span>&nbsp;({disabledOptionLabel})</span>
            )}
          </li>
        )}
        sx={sx}
        onOpen={onOpen}
        onClose={onClose}
        hideErrors={hideErrors}
        textFieldProps={textFieldProps}
      />
      {showErrors && !hideErrors && <ErrorDisplay error={error} />}
    </>
  );
});
